import { ReactNode } from 'react';
import styles from './AnalyticsTable.module.scss';
import { VdsCard, VdsIcon, VdsTooltip } from '@volt/volt-extended-components-react';
import { NoResults } from '@dmc/ui';
import { useTranslation } from 'react-i18next';

const DEFAULT_VISIBLE_ROWS = 15;

interface AnalyticsTableProps {
  title: string;
  metrics?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  hasData: boolean;
  noDataDescription: string;
  maxVisibleRows?: number;
  apiResultLimit?: number;
}

export function AnalyticsTable({
  title,
  metrics,
  actions,
  children,
  hasData,
  noDataDescription,
  maxVisibleRows = DEFAULT_VISIBLE_ROWS,
  apiResultLimit,
}: AnalyticsTableProps) {
  const { t } = useTranslation();

  const safeMaxVisibleRows = maxVisibleRows < 1 ? DEFAULT_VISIBLE_ROWS : maxVisibleRows;

  return (
    <VdsCard padding="spacing-05">
      <article className={styles.container}>
        <header className={styles.header}>
          <div className={styles.header__top}>
            <div className={styles.header__title}>
              <span>{title}</span>
              {apiResultLimit && (
                <VdsTooltip
                  id="table-limit-tooltip"
                  event="hover"
                  max-width="200px"
                  position="top"
                  type="standard"
                  text={t('ShowingLimitedResults', { limit: apiResultLimit })}
                >
                  <VdsIcon
                    slot="tooltip-trigger"
                    svgIcon="information"
                    size="18px"
                    color="color-icon-medium"
                  />
                </VdsTooltip>
              )}
            </div>

            {actions && <div className={styles.actions}>{actions}</div>}
          </div>
          {metrics && <div className={styles.metrics}>{metrics}</div>}
        </header>

        <div
          className={styles.table}
          style={
            {
              '--cas-max-visible-table-rows': safeMaxVisibleRows,
            } as React.CSSProperties
          }
        >
          {hasData ? children : <NoResults description={noDataDescription} />}
        </div>
      </article>
    </VdsCard>
  );
}

export default AnalyticsTable;
